import './MatchedResponsesModal.scss';
import { JSX, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { Form } from 'react-bootstrap';
import { MdInfoOutline } from 'react-icons/md';
import { useSetError } from '@src/redux/modals';
import { PerformanceContext } from '@src/utils/performance-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { AlertDialog } from '@Components/AlertDialog';
import { Intent } from '@Components/designConstants';
import {
  HIDE_MATCHED_RESPONSES_ALERT_KEY,
  downloadMatchedResponses
} from './matchedResponsesUtils';

interface MatchedResponsesModalProps {
  showMatchedResponsesModal: boolean;
  setShowMatchedResponsesModal: (show: boolean) => void;
  start?: string;
  end?: string;
}

export const MatchedResponsesModal = ({
  showMatchedResponsesModal,
  setShowMatchedResponsesModal,
  start,
  end
}: MatchedResponsesModalProps): JSX.Element => {
  const setError = useSetError();
  const { impsBetween, prefix, globalKpi: kpi } = useContext(PerformanceContext);
  const shouldEnableGraphPerformance = !useExperimentFlag('showIPStreamingPerformance');

  // AlertDialog hides right after primaryAction is called, without awaiting it. Ignore that hide
  // while the download runs so `loading` shows, and a failed download leaves the dialog open.
  const isDownloadingRef = useRef(false);
  const hideUnlessDownloading = useCallback(() => {
    if (!isDownloadingRef.current) {
      setShowMatchedResponsesModal(false);
    }
  }, [setShowMatchedResponsesModal]);

  const [status, setStatus] = useState<'' | 'Requesting' | 'Generating'>('');
  const [hideAlertCheckbox, setHideAlertCheckbox] = useState<boolean>(false);

  // If the user opted to hide this dialog, auto-run the primary action and close
  useEffect(() => {
    if (
      showMatchedResponsesModal &&
      localStorage.getItem(HIDE_MATCHED_RESPONSES_ALERT_KEY) === 'true'
    ) {
      (async () => {
        try {
          await downloadMatchedResponses({
            kpi,
            start,
            end,
            impsBetween,
            prefix: prefix as S.Prefix,
            isGraph: shouldEnableGraphPerformance === true,
            overloadFilenameKpi: kpi,
            setStatus,
            setError
          });
        } catch (e) {
          setError({
            reportError: e,
            message: e instanceof Error ? e.message : String(e)
          });
        } finally {
          setShowMatchedResponsesModal(false);
        }
      })();
    }
    if (!showMatchedResponsesModal) {
      setHideAlertCheckbox(false);
    }
  }, [
    showMatchedResponsesModal,
    setShowMatchedResponsesModal,
    kpi,
    start,
    end,
    impsBetween,
    prefix,
    shouldEnableGraphPerformance,
    setError,
    setStatus,
    hideAlertCheckbox
  ]);

  return (
    <AlertDialog
      intent={Intent.WARNING}
      show={showMatchedResponsesModal}
      onHide={hideUnlessDownloading}
      icon={<MdInfoOutline color='black' />}
      title='Please Note'
      body={
        <div>
          <p>
            The number of records in this file will not match the volume figures you see in the
            Bliss Point UI. The records in this file are the &ldquo;raw&rdquo; matches made on
            resolvable identifiers; because some impressions are served to, and some traffic is
            received from, irresolvable identifiers, we apply a correction factor to statistically
            de-bias the final volume calculations. For additional details on this correction, please
            refer to{' '}
            <a
              href='https://cdn.blisspointmedia.com/reference/BPM_Presentation_StreamingAttribution.pdf'
              target='_blank'
              rel='noopener noreferrer'
            >
              Streaming Attribution with Sampling
            </a>
            . If you have further questions on this topic please reach out to your Tinuiti team.
          </p>
          {['streaming'].includes(prefix) && (
            <p>
              As a reminder, this file will omit all Disney/Hulu matches per our contractual
              agreements with the publisher.
            </p>
          )}
        </div>
      }
      primaryAction={{
        label: 'Okay',
        onClick: async () => {
          isDownloadingRef.current = true;
          try {
            if (hideAlertCheckbox) {
              localStorage.setItem(HIDE_MATCHED_RESPONSES_ALERT_KEY, 'true');
            }
            await downloadMatchedResponses({
              kpi,
              start,
              end,
              impsBetween,
              prefix: prefix as S.Prefix,
              isGraph: shouldEnableGraphPerformance === true,
              overloadFilenameKpi: kpi,
              setStatus,
              setError
            });
            isDownloadingRef.current = false;
            setShowMatchedResponsesModal(false);
          } catch (e) {
            setError({
              reportError: e,
              message: e instanceof Error ? e.message : String(e)
            });
            // The dialog stays open on failure so the user can retry.
            isDownloadingRef.current = false;
          }
        }
      }}
      secondaryAction={{ label: 'Cancel', onClick: () => setShowMatchedResponsesModal(false) }}
      className='matched-responses-modal'
      passthroughProps={{ dialogClassName: 'matched-responses-modal-dialog' }}
      loading={Boolean(status)}
      additionalFooterContent={
        <div className='hide-alert-checkbox-container'>
          <Form.Check
            checked={hideAlertCheckbox}
            onChange={e => setHideAlertCheckbox(e.target.checked)}
            label='Do not show again'
          />
        </div>
      }
    />
  );
};
